/**
 * Browser utility functions
 */

// The server's language codes (the backend's `Language` literal)
export const SERVER_LANGUAGES = ['en', 'es', 'nl', 'de', 'it', 'fr', 'pt', 'ru', 'zh-hans', 'zh-hant', 'ja', 'ko'];

// Chinese regions that use traditional characters; other zh locales map to
// simplified
const TRADITIONAL_CHINESE_REGIONS = ['tw', 'hk', 'mo'];

/**
 * Maps a browser locale to a server language code ('en-US' → 'en',
 * 'zh-CN'/'zh-SG' → 'zh-hans', 'zh-TW'/'zh-HK' → 'zh-hant').
 * @param {string} locale - BCP 47 locale, e.g. navigator.language
 * @param {string[]} [available] - the server's language codes (from
 *   registration_data); SERVER_LANGUAGES when missing or empty. A code not
 *   in it maps to 'en'
 * @returns {string} server language code
 */
export const toServerLanguage = (locale, available) => {
  // an empty or missing list (registration_data not loaded) falls back too
  const codes = available && available.length > 0 ? available : SERVER_LANGUAGES;
  const parts = (locale || '').toLowerCase().replace(/_/g, '-').split('-');
  const base = parts[0];
  let code = base;
  if (base === 'zh') {
    const subtags = parts.slice(1);
    const traditional = subtags.includes('hant')
      || (!subtags.includes('hans')
          && subtags.some(s => TRADITIONAL_CHINESE_REGIONS.includes(s)));
    code = traditional ? 'zh-hant' : 'zh-hans';
  }
  if (!code) return 'en';
  if (!codes.includes(code)) return 'en';
  return code;
};

/**
 * Gets the user's browser language as a server language code (e.g. 'en',
 * 'fr', 'zh-hans')
 * @param {string[]} [available] - the server's language codes; defaults to
 *   SERVER_LANGUAGES
 * @returns {string} server language code
 */
export const getBrowserLanguage = (available) => {
  const browserLang = navigator.language || navigator.userLanguage;
  return toServerLanguage(browserLang, available);
};

/**
 * Gets the user's timezone from browser
 * @returns {string} Timezone string (e.g., 'America/Los_Angeles')
 */
export const getBrowserTimezone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};
